import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import websocketService from '../services/websocketService';
import { API_BASE } from '../config';
import { getAuthToken, setAuthToken } from '../utils/authStorage';
import { AssignmentFormModal } from './FormulationsManagement/AssignmentSystem';
import ProductAssignmentTab from './ProductAssignmentTab';
import {
  FlaskConical,
  FileSpreadsheet,
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Upload,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download
} from 'lucide-react';
import {
  fetchChemicalsPage,
  fetchChemicalIds,
  createChemical,
  updateChemical,
  deleteChemical,
  bulkDeleteChemicals,
  fetchChemicalProducts,
  createChemicalProduct,
  fetchFormulations,
  createFormulation,
  updateFormulation,
  fetchUsers,
  uploadExcelFile,
  uploadChemicalsExcel,
  downloadChemicalsExcelTemplate
} from '../api/newChemicalInventory';
import styles from './ChemicalsDashboard.module.scss';
import logger from '../utils/logger';

export default function ChemicalsDashboard() {
  const { user, userInfo } = useAuth();
  
  // Core state
  const [chemicals, setChemicals] = useState([]);
  const [chemicalsTotal, setChemicalsTotal] = useState(0);
  const [selectedChemicalIds, setSelectedChemicalIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showChemicalExcelUpload, setShowChemicalExcelUpload] = useState(false);
  const [chemicalExcelUploading, setChemicalExcelUploading] = useState(false);
  const [chemicalProducts, setChemicalProducts] = useState([]);
  const [formulations, setFormulations] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Pagination state
  const [chemicalsCurrentPage, setChemicalsCurrentPage] = useState(1);
  const [chemicalsPerPage] = useState(10);
  const [formulationsCurrentPage, setFormulationsCurrentPage] = useState(1);
  const [formulationsPerPage] = useState(10);
  
  // UI state
  const [activeTab, setActiveTab] = useState('chemicals');
  const [loading, setLoading] = useState(true);
  
  
  // Form states
  const [showChemicalForm, setShowChemicalForm] = useState(false);
  const [showFormulationForm, setShowFormulationForm] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [showAssignmentDetails, setShowAssignmentDetails] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  
  // Assignment system state
  // eslint-disable-next-line no-unused-vars
  const [assignments, setAssignments] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [currentAssignment, setCurrentAssignment] = useState(null);
  
  // Extension request state
  // eslint-disable-next-line no-unused-vars
  const [extensionRequests, setExtensionRequests] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [extensionLoading, setExtensionLoading] = useState(false);
  
  // Excel edit state
  const [excelEditData, setExcelEditData] = useState(null);
  const [excelValidationMsg, setExcelValidationMsg] = useState('');
  
  // Edit states
  const [editingChemical, setEditingChemical] = useState(null);
  const [editingFormulation, setEditingFormulation] = useState(null);
  
  // Formulation details modal state
  const [showFormulationDetails, setShowFormulationDetails] = useState(false);
  const [selectedFormulationProduct, setSelectedFormulationProduct] = useState(null);
  const [scaledQuantity, setScaledQuantity] = useState(0);
  const [editingFormulationProduct, setEditingFormulationProduct] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [savingFormulation, setSavingFormulation] = useState(false);
  
  // Removed unnecessary search and filters
  
  // Excel upload state
  const [excelData, setExcelData] = useState(null);
  const [excelPreview, setExcelPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Assignment tracking state
  // eslint-disable-next-line no-unused-vars
  const [assignmentProgress, setAssignmentProgress] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [editingAssignment, setEditingAssignment] = useState(null);
  
  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  
  // Screen size state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Search and filter state
  const [chemicalSearchTerm, setChemicalSearchTerm] = useState('');
  const [chemicalFilterType, setChemicalFilterType] = useState('all'); // all, raw, manufactured
  const [formulationSearchTerm, setFormulationSearchTerm] = useState('');
  
  // Load initial data - moved after function definitions
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
              // WebSocket connection - moved after function definitions
  
  // Server-side chemicals loading with debounced search + pagination metadata
  const loadChemicalsPage = useCallback(async () => {
    try {
      const skip = (chemicalsCurrentPage - 1) * chemicalsPerPage;
      const response = await fetchChemicalsPage({
        search: chemicalSearchTerm,
        skip,
        limit: chemicalsPerPage,
        filterType: chemicalFilterType
      });
      const list = response?.chemicals || [];
      setChemicals(list);
      setChemicalsTotal(response?.total ?? list.length);
    } catch (err) {
      logger.error('Error loading paginated chemicals:', err);
      setChemicals([]);
      setChemicalsTotal(0);
    }
  }, [chemicalSearchTerm, chemicalFilterType, chemicalsCurrentPage, chemicalsPerPage]);

  // Filter chemicals based on search term and type
  const getFilteredChemicals = () => {
    if (!chemicals || !Array.isArray(chemicals)) return [];
    return chemicals;
  };

  // Get paginated chemicals
  const getPaginatedChemicals = () => {
    return getFilteredChemicals();
  };

  // Get total pages for chemicals

  // Reset pagination when filters change
  useEffect(() => {
    setChemicalsCurrentPage(1);
    setSelectedChemicalIds(new Set());
  }, [chemicalSearchTerm, chemicalFilterType]);

  // Debounced server fetch for chemicals tab
  useEffect(() => {
    if (activeTab !== 'chemicals') return;
    const timer = setTimeout(() => {
      loadChemicalsPage();
    }, 300);
    return () => clearTimeout(timer);
  }, [activeTab, loadChemicalsPage]);

  // Get filtered formulations (products) for pagination
  const getFilteredFormulations = () => {
    let uniqueProducts = getUniqueFormulationProducts();
    
    if (formulationSearchTerm) {
      uniqueProducts = uniqueProducts.filter(product => 
        product.name && product.name.toLowerCase().startsWith(formulationSearchTerm.toLowerCase())
      );
    }
    
    // Sort by database index (id) in ascending order
    uniqueProducts = uniqueProducts.sort((a, b) => (a.id || 0) - (b.id || 0));
    
    return uniqueProducts;
  };

  // Get paginated formulations
  const getPaginatedFormulations = () => {
    const filtered = getFilteredFormulations();
    const startIndex = (formulationsCurrentPage - 1) * formulationsPerPage;
    const endIndex = startIndex + formulationsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // Get total pages for formulations

  // Reset formulations pagination when search changes
  useEffect(() => {
    setFormulationsCurrentPage(1);
  }, [formulationSearchTerm]);

  const loadAssignments = useCallback(async () => {
    try {
      logger.debug('🔍 Loading assignments...');
      const token = getAuthToken();
      logger.debug('🔑 Token present:', token ? 'Yes' : 'No');
      
      const response = await fetch(`${API_BASE}/assignments/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      logger.debug('📡 Assignments API response status:', response.status);
      logger.debug('📡 Assignments API response ok:', response.ok);

      if (response.ok) {
        const assignmentsData = await response.json();
        logger.debug('📋 Raw assignments data:', assignmentsData);
        logger.debug('📊 Assignments data type:', typeof assignmentsData);
        logger.debug('📊 Assignments is array:', Array.isArray(assignmentsData));
        logger.debug('🔢 Assignments length:', assignmentsData?.length);
        
        if (assignmentsData && assignmentsData.length > 0) {
          logger.debug('🔍 First assignment details:', assignmentsData[0]);
          logger.debug('🔍 Assignment status:', assignmentsData[0].status);
          logger.debug('🔍 Assignment progress:', assignmentsData[0].progress_percentage);
          logger.debug('🔍 Assignment time remaining:', assignmentsData[0].time_remaining);
          logger.debug('🔍 Assignment product name:', assignmentsData[0].product_name);
          logger.debug('🔍 Assignment assigned to:', assignmentsData[0].assigned_to_name);
        }
        
        // Transform assignments to match frontend format
        const transformedAssignments = assignmentsData.map(assignment => {
          logger.debug('🔄 Transforming assignment:', assignment.id, 'Status:', assignment.status);
          return {
            id: assignment.id,
            product_name: assignment.product_name,
            assigned_to_name: assignment.assigned_to_name,
            target_quantity: assignment.quantity_requested,
            target_unit: assignment.unit,
            status: assignment.status,
            progress_percentage: assignment.progress_percentage || 0,
            created_at: assignment.created_at,
            timeLimit: assignment.time_remaining || 60,
            components: [], // Will be loaded when viewing details
            is_expired: assignment.is_expired || false,
            time_remaining: assignment.time_remaining || 0,
            team_type: assignment.team_type || 'UNKNOWN'
          };
        });
        
        // Load formulation components for each assignment to calculate progress
        const assignmentsWithComponents = await Promise.all(
          transformedAssignments.map(async (assignment) => {
            try {
              const componentsResponse = await fetch(`${API_BASE}/assignments/${assignment.id}/formulation-components`, {
                headers: {
                  'Authorization': `Bearer ${getAuthToken()}`
                }
              });
              
              if (componentsResponse.ok) {
                const components = await componentsResponse.json();
                logger.debug(`📊 Loaded ${components.length} components for assignment ${assignment.id}`);
                return { ...assignment, components };
              } else {
                logger.error(`Failed to load components for assignment ${assignment.id}:`, componentsResponse.status);
                return assignment;
              }
            } catch (err) {
              logger.error(`Error loading components for assignment ${assignment.id}:`, err);
              return assignment;
            }
          })
        );
        
        logger.debug('✅ Transformed assignments with components:', assignmentsWithComponents);
        setAssignments(assignmentsWithComponents);
      } else {
        const errorText = await response.text();
        logger.error('❌ Failed to load assignments:', response.status, errorText);
        
        if (response.status === 401) {
          logger.error('Authentication failed when loading assignments');
        }
      }
    } catch (err) {
      logger.error('❌ Error loading assignments:', err);
    }
  }, [setAssignments]);
  
  // Load formulation components for an assignment

  // Load extension requests
  const loadExtensionRequests = useCallback(async () => {
    try {
      setExtensionLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/assignments/extension-requests/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch extension requests');
      }
      
      const data = await response.json();
      setExtensionRequests(data);
    } catch (err) {
      logger.error('Error loading extension requests:', err);
    } finally {
      setExtensionLoading(false);
    }
  }, [setExtensionLoading, setExtensionRequests]);

  // Load initial data function - moved before useEffect
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      logger.debug('Loading initial data...');
      
      // Load chemicals first (this is working)
      try {
        // Initial load must NOT depend on live search state.
        // Otherwise, every keystroke can recreate loadInitialData and trigger
        // a full dashboard reload (parent-level `loading` screen).
        const response = await fetchChemicalsPage({
          search: '',
          skip: 0,
          limit: chemicalsPerPage,
          filterType: 'all',
        });

        const list = response?.chemicals || [];
        setChemicals(list);
        setChemicalsTotal(response?.total ?? list.length);

        logger.debug('Chemicals loaded successfully (initial)');
      } catch (err) {
        logger.error('Error loading chemicals:', err);
        setChemicals([]);
      }
      
      // Load other data separately to avoid blocking chemicals
      try {
        logger.debug('🔍 Fetching chemical products...');
        const productsData = await fetchChemicalProducts();
        logger.debug('📦 Chemical products API response:', productsData);
        logger.debug('📊 Products data type:', typeof productsData);
        logger.debug('📋 Products is array:', Array.isArray(productsData));
        logger.debug('🔢 Products length:', productsData?.length || 'undefined');
        
        if (productsData && Array.isArray(productsData)) {
          setChemicalProducts(productsData);
          logger.debug('✅ Chemical products set successfully:', productsData.length);
        } else {
          logger.error('❌ Invalid products data format:', productsData);
          setChemicalProducts([]);
        }
      } catch (err) {
        logger.error('❌ Error loading chemical products:', err);
        logger.error('🔍 Error details:', err.message);
        setChemicalProducts([]);
      }
      
      try {
        const formulationsData = await fetchFormulations();
        logger.debug('Formulations loaded successfully:', formulationsData);
        setFormulations(formulationsData);
      } catch (err) {
        logger.error('Error loading formulations:', err);
        setFormulations([]);
      }
      
      try {
        const usersData = await fetchUsers();
        logger.debug('Users loaded successfully:', usersData);
        setUsers(usersData);
      } catch (err) {
        logger.error('Error loading users:', err);
        setUsers([]);
      }
      
    } catch (err) {
      logger.error('Error in loadInitialData:', err);
    } finally {
      setLoading(false);
    }
  }, [
    setLoading,
    setChemicals,
    setChemicalsTotal,
    setChemicalProducts,
    setFormulations,
    setUsers,
    chemicalsPerPage,
  ]);

  // Notification handler - moved before functions that use it
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 9)]); // Keep last 10
  }, [setNotifications]);

  // WebSocket message handlers - moved before useEffect
  const handleComponentCompleted = useCallback((data) => {
    addNotification({
      type: 'success',
      title: 'Component Completed',
      message: `${data.component_code} completed by ${data.team_member}`,
      timestamp: new Date()
    });
    
    // Update assignment progress
    setAssignmentProgress(prev => ({
      ...prev,
      [data.assignment_id]: {
        ...prev[data.assignment_id],
        [data.component_code]: { completed: true, quantity: data.quantity_added }
      }
    }));
  }, [addNotification, setAssignmentProgress]);
  
  const handleTimerExpired = useCallback((data) => {
    addNotification({
      type: 'warning',
      title: 'Timer Expired',
      message: `Assignment for ${data.product_name} has expired`,
      timestamp: new Date()
    });
  }, [addNotification]);
  
  const handleOtpExtensionRequested = useCallback((data) => {
    addNotification({
      type: 'info',
      title: 'OTP Extension Requested',
      message: `${data.user_name} requested OTP extension for ${data.product_name}`,
      timestamp: new Date()
    });
  }, [addNotification]);
  
  const handleAssignmentCompleted = useCallback((data) => {
    addNotification({
      type: 'success',
      title: 'Assignment Completed',
      message: `${data.product_name} completed by ${data.team_member}`,
      timestamp: new Date()
    });
    
    // Refresh assignments
    loadInitialData();
  }, [addNotification, loadInitialData]);

  // Load initial data
  useEffect(() => {
    if (user && userInfo) {
      loadInitialData(); 
      loadAssignments(); // Load existing assignments
      loadExtensionRequests(); // Load extension requests
    }
  }, [user, userInfo, loadInitialData, loadAssignments, loadExtensionRequests]);
  

  const handleWebSocketMessage = useCallback((data) => {
    logger.debug('📨 Admin WebSocket message received:', data);
    logger.debug('📨 Message type:', data.type);
    logger.debug('📨 Message data:', JSON.stringify(data, null, 2));
    
    switch (data.type) {
      case 'connection_established':
        logger.debug('✅ Admin WebSocket connection established');
        setWsConnected(true);
        break;
        
      case 'connection_info':
        logger.debug('ℹ️ Admin connection info:', data.message);
        setWsConnected(true);
        break;
        
      case 'pong':
        logger.debug('🏓 Admin pong received');
        break;
        
      case 'assignment_created':
        logger.debug('🆕 New assignment created');
        logger.debug('🆕 Assignment data:', data);
        loadAssignments(); // Refresh assignments
        addNotification({
          type: 'info',
          title: 'New Assignment',
          message: 'A new assignment has been created',
          timestamp: new Date()
        });
        break;
        
      case 'assignment_updated':
        logger.debug('🔄 Assignment updated');
        logger.debug('🔄 Assignment data:', data);
        loadAssignments(); // Refresh assignments
        break;
        
      case 'assignment_completed':
        logger.debug('✅ Assignment completed by', data.updated_by);
        logger.debug('✅ Assignment data:', data);
        loadAssignments(); // Refresh assignments
        addNotification({
          type: 'success',
          title: 'Assignment Completed',
          message: `Assignment completed by ${data.updated_by}`,
          timestamp: new Date()
        });
        break;
        
      case 'timer_update':
        logger.debug('⏰ Timer update received:', data);
        logger.debug('⏰ Assignment ID:', data.assignment_id);
        logger.debug('⏰ Time remaining:', data.time_remaining);
        // Update timer for specific assignment
        if (data.assignment_id) {
          setAssignments(prev => {
            logger.debug('⏰ Updating timer for assignment:', data.assignment_id);
            const updated = prev.map(assignment => 
              assignment.id === data.assignment_id 
                ? { ...assignment, time_remaining: data.time_remaining }
                : assignment
            );
            logger.debug('⏰ Updated assignments:', updated);
            return updated;
          });
        }
        break;
        
      case 'progress_update':
        logger.debug('📊 Progress update received:', data);
        logger.debug('📊 Assignment ID:', data.assignment_id);
        logger.debug('📊 Progress percentage:', data.progress_percentage);
        // Update progress for specific assignment
        if (data.assignment_id) {
          setAssignments(prev => {
            logger.debug('📊 Updating progress for assignment:', data.assignment_id);
            const updated = prev.map(assignment => 
              assignment.id === data.assignment_id 
                ? { ...assignment, progress_percentage: data.progress_percentage }
                : assignment
            );
            logger.debug('📊 Updated assignments:', updated);
            return updated;
          });
        }
        break;
        
      case 'component_completed':
        logger.debug('🧪 Component completed by', data.completed_by);
        addNotification({
          type: 'success',
          title: 'Component Completed',
          message: `Component completed by ${data.completed_by}`,
          timestamp: new Date()
        });
        break;
        
      case 'help_requested':
        logger.debug('🆘 Help requested by', data.user_id, ':', data.message);
        addNotification({
          type: 'warning',
          title: 'Help Requested',
          message: `Help requested: ${data.message}`,
          timestamp: new Date()
        });
        break;
        
      case 'otp_expired':
        logger.debug('⏰ OTP expired');
        addNotification({
          type: 'error',
          title: 'OTP Expired',
          message: 'An OTP has expired',
          timestamp: new Date()
        });
        break;
        
      case 'error':
        logger.error('❌ WebSocket error:', data.message);
        addNotification({
          type: 'error',
          title: 'WebSocket Error',
          message: data.message,
          timestamp: new Date()
        });
        break;
        
      // Legacy message types for backward compatibility
      case 'COMPONENT_COMPLETED':
        handleComponentCompleted(data);
        break;
      case 'TIMER_EXPIRED':
        handleTimerExpired(data);
        break;
      case 'OTP_EXTENSION_REQUESTED':
        handleOtpExtensionRequested(data);
        break;
      case 'ASSIGNMENT_COMPLETED':
        handleAssignmentCompleted(data);
        break;
      case 'PROGRESS_UPDATE':
        handleProgressUpdate(data);
        break;
        
      default:
        logger.debug('❓ Unknown WebSocket message type:', data.type);
    }
  }, [loadAssignments, addNotification, setAssignments, setWsConnected, handleAssignmentCompleted, handleComponentCompleted, handleOtpExtensionRequested, handleTimerExpired]);

  // WebSocket connection
  useEffect(() => {
    if (user && userInfo && userInfo.role_id === 1) { // Admin only
      const connectWebSocket = async () => {
        try {
          // Get fresh token
          const token = await user.getIdToken(true);
          setAuthToken(token);

          // Connect to WebSocket with fresh token
          await websocketService.connect(
            'admin', // team type
            userInfo.id.toString(), // user ID
            token,
            // onMessage
            (data) => {
              logger.debug('📨 Admin WebSocket message received:', data);
              handleWebSocketMessage(data);
            },
            // onError
            (error) => {
              logger.error('❌ Admin WebSocket error:', error);
              setWsConnected(false);
            },
            // onClose
            (event) => {
              logger.debug('🔌 Admin WebSocket closed:', event.code, event.reason);
              setWsConnected(false);
            }
          );

          // Check connection status
          const checkConnection = () => {
            const status = websocketService.getConnectionStatus('admin', userInfo.id.toString());
            setWsConnected(status === 'connected');
          };

          // Check connection status periodically
          const interval = setInterval(checkConnection, 1000);
          checkConnection(); // Initial check

          return () => {
            clearInterval(interval);
            websocketService.disconnect('admin', userInfo.id.toString());
          };
        } catch (error) {
          logger.error('❌ Failed to connect WebSocket:', error);
        }
      };

      // Call the async function
      connectWebSocket().catch(error => {
        logger.error('❌ Failed to connect WebSocket:', error);
      });
    }
  }, [user, userInfo, handleWebSocketMessage]);
  
  const handleProgressUpdate = (data) => {
    setAssignmentProgress(prev => ({
      ...prev,
      [data.assignment_id]: {
        ...prev[data.assignment_id],
        ...data.progress
      }
    }));
  };
  
  // Chemical management functions
  const handleCreateChemical = async (chemicalData) => {
    try {
      const newChemical = await createChemical(chemicalData);
      logger.debug('Chemical created successfully:', newChemical);
      
      setChemicals(prev => {
        const updated = [...prev, newChemical];
        logger.debug('Updated chemicals state:', updated);
        return updated;
      });
      
      setShowChemicalForm(false);
      addNotification({
        type: 'success',
        title: 'Chemical Created',
        message: `Chemical ${newChemical.name} created successfully`,
        timestamp: new Date()
      });
          } catch (err) {
        logger.error('Error creating chemical:', err);
      }
  };

  const handleUploadChemicalsExcel = async (file) => {
    setChemicalExcelUploading(true);
    try {
      const result = await uploadChemicalsExcel(file);
      await loadChemicalsPage();
      addNotification({
        type: result.created > 0 ? 'success' : 'warning',
        title: 'Chemicals Excel Upload',
        message: result.message,
        timestamp: new Date()
      });
      return result;
    } catch (err) {
      logger.error('Error uploading chemicals Excel:', err);
      addNotification({
        type: 'error',
        title: 'Excel Upload Failed',
        message: err.message || 'Failed to upload chemicals Excel file',
        timestamp: new Date()
      });
      throw err;
    } finally {
      setChemicalExcelUploading(false);
    }
  };
  
  const handleUpdateChemical = async (id, chemicalData) => {
    try {
      const updatedChemical = await updateChemical(id, chemicalData);
      setChemicals(prev => prev.map(c => c.id === id ? updatedChemical : c));
      setEditingChemical(null);
      addNotification({
        type: 'success',
        title: 'Chemical Updated',
        message: `Chemical ${updatedChemical.name} updated successfully`,
        timestamp: new Date()
      });
    } catch (err) {
      logger.error('Error updating chemical:', err);
    }
  };
  
  const handleDeleteChemical = async (id) => {
    if (!window.confirm('Are you sure you want to delete this chemical?')) return;

    try {
      await deleteChemical(id);
      setSelectedChemicalIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      addNotification({
        type: 'success',
        title: 'Chemical Deleted',
        message: 'Chemical deleted successfully',
        timestamp: new Date()
      });
      await loadChemicalsPage();
    } catch (err) {
      logger.error('Error deleting chemical:', err);
    }
  };

  const toggleChemicalSelection = (id) => {
    setSelectedChemicalIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectPage = () => {
    const pageIds = chemicals.map(c => c.id);
    const allSelected = pageIds.length > 0 && pageIds.every(id => selectedChemicalIds.has(id));
    setSelectedChemicalIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach(id => next.delete(id));
      } else {
        pageIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handleSelectAllMatching = async () => {
    try {
      const data = await fetchChemicalIds({
        search: chemicalSearchTerm,
        filterType: chemicalFilterType
      });
      setSelectedChemicalIds(new Set(data.ids || []));
    } catch (err) {
      logger.error('Error selecting all chemicals:', err);
    }
  };

  const handleBulkDeleteChemicals = async () => {
    const idsToDelete = Array.from(selectedChemicalIds);
    const count = idsToDelete.length;
    if (count === 0) return;

    if (!window.confirm(
      `Delete ${count} chemical(s)? This will also remove related purchases, stock, alerts, formulations and products. This cannot be undone.`
    )) return;

    try {
      setBulkDeleting(true);
      const result = await bulkDeleteChemicals({
        chemicalIds: idsToDelete,
        deleteAll: false
      });
      setSelectedChemicalIds(new Set());
      addNotification({
        type: 'success',
        title: 'Chemicals Deleted',
        message: result.message || `${count} chemical(s) deleted successfully`,
        timestamp: new Date()
      });

      const remainingOnPage = chemicals.filter(c => !idsToDelete.includes(c.id)).length;
      if (remainingOnPage === 0 && chemicalsCurrentPage > 1) {
        setChemicalsCurrentPage(prev => Math.max(1, prev - 1));
      } else {
        await loadChemicalsPage();
      }
    } catch (err) {
      logger.error('Error bulk deleting chemicals:', err);
      addNotification({
        type: 'error',
        title: 'Bulk Delete Failed',
        message: err.message || 'Failed to delete chemicals',
        timestamp: new Date()
      });
    } finally {
      setBulkDeleting(false);
    }
  };
  
  // Formulation management functions
  const findChemicalByName = useCallback(async (name) => {
    if (!name || !String(name).trim()) return null;
    const normalized = String(name).trim().toLowerCase();

    const localMatch = chemicals.find(c => c.name?.toLowerCase() === normalized);
    if (localMatch) return localMatch;

    try {
      const response = await fetchChemicalsPage({
        search: String(name).trim(),
        skip: 0,
        limit: 100,
        filterType: 'all'
      });
      const list = response?.chemicals || [];
      return list.find(c => c.name?.toLowerCase() === normalized) || null;
    } catch (err) {
      logger.error('Error looking up chemical by name:', err);
      return null;
    }
  }, [chemicals]);

  const getMissingComponentChemicals = useCallback(async (componentsList) => {
    const missing = [];
    const foundMap = new Map();

    for (const component of componentsList || []) {
      const name = component?.name?.trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (foundMap.has(key)) continue;

      const chemical = await findChemicalByName(name);
      if (chemical) {
        foundMap.set(key, chemical);
      } else {
        missing.push(name);
      }
    }

    return { missing, foundMap };
  }, [findChemicalByName]);

  const handleCreateFormulation = async (formulationData) => {
    try {
      logger.debug('🚀 Starting formulation creation process...');
      logger.debug('📦 Formulation data received:', formulationData);
      
      // Extract product and components data
      const { product, components } = formulationData;
      logger.debug('📦 Product data:', product);
      logger.debug('🧪 Components data:', components);
      logger.debug('🔍 Current chemicals count:', chemicals.length);

      // Validate all component chemicals exist in Chemical Management first
      const { missing, foundMap } = await getMissingComponentChemicals(components);
      if (missing.length > 0) {
        const missingList = missing.join(', ');
        addNotification({
          type: 'error',
          title: 'Chemical Not Found',
          message: `These chemicals are not in Chemical Management: ${missingList}. Please add them first, then create the formulation.`,
          timestamp: new Date()
        });
        return;
      }
      
      // Step 1: Check if product chemical already exists
      logger.debug('🔍 Step 1: Checking for existing product chemical...');
      let productChemical = chemicals.find(c => c.name === product.name);
      
      if (!productChemical) {
        logger.debug('🆕 Product chemical not found, creating new one...');
        logger.debug('📊 Creating chemical with data:', {
          name: product.name,
          unit: product.unit,
          available_qty: 0.0,
          threshold_qty: 0.0,
          is_manufactured: true
        });
        
        // Create the manufactured chemical (product) if it doesn't exist
        productChemical = await createChemical({
          name: product.name,
          unit: product.unit,
          available_qty: 0.0, // No stock initially
          threshold_qty: 0.0, // No threshold initially
          is_manufactured: true // This is a manufactured chemical
        });
        
        logger.debug('✅ Product chemical created successfully:', productChemical);
        
        // Refresh chemicals list
        await loadChemicalsPage();
        logger.debug('🔄 Chemicals list refreshed');
      } else {
        logger.debug('✅ Using existing product chemical:', productChemical);
      }
      
      // Step 2: Check if chemical product already exists
      logger.debug('🔍 Step 2: Checking for existing chemical product...');
      let chemicalProduct = chemicalProducts.find(p => p.name === product.name);
      
      if (!chemicalProduct) {
        logger.debug('🆕 Chemical product not found, creating new one...');
        logger.debug('📊 Chemical product data:', {
          chemical_id: productChemical.id,
          name: product.name,
          base_composition_qty: product.base_composition_qty,
          unit: product.unit,
          note: `Created from formulation: ${product.name}`,
          created_by: userInfo?.id || 1
        });
        
        chemicalProduct = await createChemicalProduct({
          chemical_id: productChemical.id,
          name: product.name,
          base_composition_qty: product.base_composition_qty,
          unit: product.unit,
          note: `Created from formulation: ${product.name}`,
          created_by: userInfo?.id || 1
        });
        
        logger.debug('✅ Chemical product created successfully:', chemicalProduct);
      } else {
        logger.debug('✅ Using existing chemical product:', chemicalProduct);
      }
      
      // Step 3: Create formulations for existing component chemicals only
      const createdFormulations = [];
      
      for (const component of components) {
        logger.debug(`\n🧪 Processing component: ${component.name}`);
        
        const componentChemical = foundMap.get(component.name.trim().toLowerCase());
        if (!componentChemical) {
          addNotification({
            type: 'error',
            title: 'Chemical Not Found',
            message: `Chemical "${component.name}" is not in Chemical Management. Please add it first.`,
            timestamp: new Date()
          });
          return;
        }

        logger.debug(`✅ Using existing component chemical "${component.name}":`, componentChemical);
        
        // Create formulation linking product to component
        logger.debug('🔄 Step 3: Creating formulation for component:', component.name);
        logger.debug('📊 Formulation data being sent:', {
          product_id: chemicalProduct.id,
          component_chemical_id: componentChemical.id,
          quantity_required: component.quantity,
          unit: component.unit
        });
        
        try {
          logger.debug('📡 Calling createFormulation API...');
          const formulation = await createFormulation({
            product_id: chemicalProduct.id,
            component_chemical_id: componentChemical.id,
            quantity_required: component.quantity,
            unit: component.unit
          });
          
          logger.debug('✅ Formulation created successfully:', formulation);
          createdFormulations.push(formulation);
        } catch (formulationError) {
          logger.error('❌ Error creating formulation for component:', component.name);
          logger.error('🔍 Formulation error details:', formulationError);
          logger.error('📊 Failed data:', {
            product_id: chemicalProduct.id,
            component_chemical_id: componentChemical.id,
            quantity_required: component.quantity,
            unit: component.unit
          });
          throw formulationError; // Re-throw to be caught by outer try-catch
        }
      }
      
      logger.debug('\n🔄 Refreshing data after all formulations created...');
      
      // Refresh data
      const [productsData, formulationsData] = await Promise.all([
        fetchChemicalProducts(),
        fetchFormulations()
      ]);
      
      logger.debug('✅ Data refresh completed:');
      logger.debug('📦 Products count:', productsData.length);
      logger.debug('🧪 Formulations count:', formulationsData.length);
      
      setChemicalProducts(productsData);
      setFormulations(formulationsData);
      
      // Close modal and show success
      setShowFormulationForm(false);
      logger.debug('🎉 Formulation creation process completed successfully!');
      addNotification({
        type: 'success',
        title: 'Formulation Created',
        message: `Formulation "${product.name}" created successfully with ${components.length} components`,
        timestamp: new Date()
      });
      
    } catch (err) {
      logger.error('❌ CRITICAL ERROR in formulation creation process:');
      logger.error('🔍 Error details:', err);
      logger.error('📊 Error message:', err.message);
      logger.error('📋 Error stack:', err.stack);
      logger.error('🚨 Formulation creation failed completely');
      
      addNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to create formulation: ${err.message}`,
        timestamp: new Date()
      });
    }
  };
  
  const handleUpdateFormulation = async (id, formulationData) => {
    try {
      const updatedFormulation = await updateFormulation(id, formulationData);
      setFormulations(prev => prev.map(f => f.id === id ? updatedFormulation : f));
      setEditingFormulation(null);
      addNotification({
        type: 'success',
        title: 'Formulation Updated',
        message: `Formulation updated successfully`,
        timestamp: new Date()
      });
    } catch (err) {
      logger.error('Error updating formulation:', err);
    }
  };
  
  
  const handleExcelUpload = async (file) => {
    try {
      setUploading(true);
      logger.debug('[ChemicalsDashboard] handleExcelUpload start');
      const result = await uploadExcelFile(file);
      logger.debug('[ChemicalsDashboard] uploadExcelFile result:', result);
      setExcelPreview(result.parsed_data);
      setExcelData(result.parsed_data);
      // Initialize editable copy
      setExcelEditData({
        product_name: result.parsed_data.product_name || '',
        base_composition_qty: result.parsed_data.base_composition_qty || 0,
        unit: result.parsed_data.unit || 'g',
        components: (result.parsed_data.components || []).map(c => ({
          code: c.code || '',
          quantity: c.quantity || 0,
          unit: c.unit || (result.parsed_data.unit || 'g')
        }))
      });
      // Reset validation
      setExcelValidationMsg('');
      setShowExcelUpload(false);
      addNotification({
        type: 'success',
        title: 'Excel Uploaded',
        message: 'Excel file parsed successfully. Please review and approve.',
        timestamp: new Date()
      });
    } catch (err) {
      logger.error('[ChemicalsDashboard] Error uploading Excel:', err);
    } finally {
      logger.debug('[ChemicalsDashboard] handleExcelUpload end (uploading=false)');
      setUploading(false);
    }
  };

  // Excel edit helpers
  const getExcelComponentsTotal = () => {
    if (!excelEditData) return 0;
    const total = excelEditData.components.reduce((sum, comp) => sum + (parseFloat(comp.quantity) || 0), 0);
    return parseFloat(total.toFixed(4));
  };

  const validateExcelEditData = () => {
    if (!excelEditData) return '';
    const total = getExcelComponentsTotal();
    const base = parseFloat(excelEditData.base_composition_qty) || 0;
    if (excelEditData.product_name.trim().length === 0) return 'Product name cannot be empty';
    if (excelEditData.components.length === 0) return 'Add at least one component';
    if (excelEditData.components.some(c => !c.code.trim())) return 'All component codes must be filled';
    if (Math.abs(total - base) > 0.01) return `Component total (${total}) must equal base composition (${base})`;
    return '';
  };

  const setExcelBaseToSum = () => {
    if (!excelEditData) return;
    const total = getExcelComponentsTotal();
    setExcelEditData(prev => ({ ...prev, base_composition_qty: total }));
    setExcelValidationMsg('');
  };

  const addExcelComponent = () => {
    if (!excelEditData) return;
    setExcelEditData(prev => ({
      ...prev,
      components: [...prev.components, { code: '', quantity: 0, unit: prev.unit || 'g' }]
    }));
  };

  const deleteExcelComponent = (index) => {
    if (!excelEditData) return;
    setExcelEditData(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index)
    }));
  };

  const updateExcelComponentCode = (index, value) => {
    setExcelEditData(prev => ({
      ...prev,
      components: prev.components.map((c, i) => i === index ? { ...c, code: value } : c)
    }));
  };

  const updateExcelComponentQty = (index, value) => {
    const qty = parseFloat(value);
    setExcelEditData(prev => ({
      ...prev,
      components: prev.components.map((c, i) => i === index ? { ...c, quantity: isNaN(qty) ? 0 : qty } : c)
    }));
  };
  
  const handleApproveExcel = async () => {
    // Use the edited data if available
    const payload = excelEditData || excelData;
    if (!payload) return;
    const msg = validateExcelEditData();
    setExcelValidationMsg(msg);
    if (msg) return;
    
    try {
      setUploading(true);
      logger.debug('[ChemicalsDashboard] Approve Excel payload:', payload);
      // Call the approve endpoint
      const response = await fetch(`${API_BASE}/excel/approve-formulation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(payload)
      });
      
      logger.debug('[ChemicalsDashboard] Approve response status:', response.status, 'ok:', response.ok);
      if (!response.ok) {
        const errorData = await response.json();
        logger.error('[ChemicalsDashboard] Approve error JSON:', errorData);
        throw new Error(errorData.detail || 'Failed to approve formulation');
      }
      
      const result = await response.json();
      logger.debug('[ChemicalsDashboard] Approve result:', result);
      setExcelPreview(null);
      setExcelData(null);
      setExcelEditData(null);
      setExcelValidationMsg('');
      
      // Refresh data
      logger.debug('[ChemicalsDashboard] Refreshing data after approve');
      loadInitialData();
      
      addNotification({
        type: 'success',
        title: 'Formulation Approved',
        message: result.message,
        timestamp: new Date()
      });
    } catch (err) {
      logger.error('[ChemicalsDashboard] Error approving Excel:', err);
      addNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to approve formulation: ${err.message}`,
        timestamp: new Date()
      });
    } finally {
      logger.debug('[ChemicalsDashboard] handleApproveExcel end (uploading=false)');
      setUploading(false);
    }
  };
  
  // Assignment management functions
  const handleCreateAssignment = async (assignmentData) => {
    try {
      logger.debug('Creating assignment:', assignmentData);
      
      // Transform assignment data to match backend schema
      const backendAssignmentData = {
        product_id: assignmentData.productId,
        assigned_to_user_id: assignmentData.assigned_to_id,
        quantity_requested: parseFloat(assignmentData.targetQuantity),
        unit: assignmentData.targetUnit,
        time_allotted_minutes: parseInt(assignmentData.timeLimit)
      };

      logger.debug('Sending to backend:', backendAssignmentData);
      logger.debug('Auth token:', getAuthToken() ? 'Present' : 'Missing');
      logger.debug('User info:', userInfo);

      // Make actual API call to create assignment
      const response = await fetch(`${API_BASE}/assignments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(backendAssignmentData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Assignment creation failed:', response.status, errorText);
        
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else {
          throw new Error(`HTTP error! status: ${response.status}: ${errorText}`);
        }
      }

      const newAssignment = await response.json();
      logger.debug('Assignment created successfully:', newAssignment);
      
      // Fetch assignment details to get components and progress
      const detailsResponse = await fetch(`${API_BASE}/assignments/${newAssignment.id}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      
      if (detailsResponse.ok) {
        const assignmentDetails = await detailsResponse.json();
        logger.debug('Assignment details:', assignmentDetails);
        
        // Transform the assignment details to match frontend format
        const frontendAssignment = {
          id: newAssignment.id,
          product_name: assignmentData.product_name,
          assigned_to_name: assignmentData.assigned_to_name,
          target_quantity: assignmentData.targetQuantity,
          target_unit: assignmentData.targetUnit,
          baseComposition: assignmentData.baseComposition,
          baseUnit: assignmentData.baseUnit,
          components: assignmentDetails.progress || [],
          status: newAssignment.status,
          created_at: newAssignment.created_at,
          timeLimit: newAssignment.time_allotted_minutes,
          otp: newAssignment.otp_token,
          is_expired: assignmentDetails.is_expired || false,
          time_remaining: assignmentDetails.time_remaining || 0
        };
        
        logger.debug('Frontend assignment created:', frontendAssignment);
        setAssignments(prev => [...prev, frontendAssignment]);
        
        // Also refresh the assignments list to ensure consistency
        loadAssignments();
      } else {
        // Fallback: add assignment without details
        setAssignments(prev => [...prev, {
          id: newAssignment.id,
          product_name: assignmentData.product_name,
          assigned_to_name: assignmentData.assigned_to_name,
          target_quantity: assignmentData.targetQuantity,
          target_unit: assignmentData.targetUnit,
          baseComposition: assignmentData.baseComposition,
          baseUnit: assignmentData.baseUnit,
          components: [],
          status: newAssignment.status,
          created_at: newAssignment.created_at,
          timeLimit: newAssignment.time_allotted_minutes,
          otp: newAssignment.otp_token
        }]);
      }
      
      setShowAssignmentForm(false);
      
      addNotification({
        type: 'success',
        title: 'Assignment Created',
        message: `Assignment created for ${assignmentData.product_name}`,
        timestamp: new Date()
      });
    } catch (err) {
      logger.error('Error creating assignment:', err);
      addNotification({
        type: 'error',
        title: 'Assignment Failed',
        message: `Failed to create assignment: ${err.message}`,
        timestamp: new Date()
      });
    }
  };


  
  // Helper functions for formulations management
  const getUniqueFormulationProducts = () => {
    logger.debug('🔍 getUniqueFormulationProducts called - showing ALL products');
    logger.debug('📊 Current formulations:', formulations);
    logger.debug('📦 Current chemical products:', chemicalProducts);
    
    // Return ALL chemical products, not just those with formulations
    const allProducts = chemicalProducts.map(product => ({
      id: product.id,
      name: product.name,
      base_composition_qty: product.base_composition_qty,
      unit: product.unit
    }));
    
    logger.debug('✅ All products found:', allProducts);
    return allProducts;
  };

  const getFormulationComponentsCount = (productId) => {
    return formulations.filter(f => f.product_id === productId).length;
  };

  const getFormulationDetails = (productId) => {
    logger.debug('🔍 getFormulationDetails called for product ID:', productId);
    logger.debug('📦 Available chemical products:', chemicalProducts);
    logger.debug('🧪 Available formulations:', formulations);
    
    const product = chemicalProducts.find(p => p.id === productId);
    logger.debug('📦 Found product:', product);
    
    const productFormulations = formulations.filter(f => f.product_id === productId);
    logger.debug('🧪 Found formulations for product:', productFormulations);
    
    const result = {
      product,
      components: productFormulations.map(f => {
        const chemical = chemicals.find(c => c.id === f.component_chemical_id);
        logger.debug(`🔍 Looking for chemical ID ${f.component_chemical_id}:`, chemical);
        return {
          id: f.id,
          chemical_name: chemical?.name || `Chemical ${f.component_chemical_id}`,
          quantity_required: f.quantity_required,
          unit: f.unit
        };
      })
    };
    
    logger.debug('✅ Final result:', result);
    return result;
  };

  const handleViewFormulationDetails = (productId) => {
    const details = getFormulationDetails(productId);
    logger.debug('🔍 Opening formulation details for product:', productId);
    logger.debug('📦 Product details:', details);
    
    if (details && details.product) {
      setSelectedFormulationProduct(details);
      // Ensure scaledQuantity is properly initialized with a valid number
      const baseQty = details.product.base_composition_qty || 0;
      setScaledQuantity(baseQty);
      logger.debug('✅ Set scaled quantity to:', baseQty);
      
      // Initialize edit form data
      setEditFormData({
        product: {
          name: details.product.name,
          base_composition_qty: details.product.base_composition_qty,
          unit: details.product.unit
        },
        components: details.components.map(comp => ({
          name: comp.chemical_name,
          quantity: comp.quantity_required,
          unit: comp.unit
        }))
      });
      
      setShowFormulationDetails(true);
    } else {
      logger.error('❌ Invalid formulation details:', details);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Could not load formulation details',
        timestamp: new Date()
      });
    }
  };

  const handleEditFormulation = (productId) => {
    logger.debug('🔍 Edit formulation requested for product ID:', productId);
    
    // Initialize edit form data if not already set
    if (!editFormData && selectedFormulationProduct) {
      setEditFormData({
        product: {
          name: selectedFormulationProduct.product.name,
          base_composition_qty: selectedFormulationProduct.product.base_composition_qty,
          unit: selectedFormulationProduct.product.unit
        },
        components: selectedFormulationProduct.components.map(comp => ({
          name: comp.chemical_name,
          quantity: comp.quantity_required,
          unit: comp.unit
        }))
      });
    }
    
    // Toggle edit mode for the current modal
    setEditingFormulationProduct(productId);
    
    addNotification({
      type: 'success',
      title: 'Edit Mode',
      message: 'Formulation is now in edit mode. Make your changes and click Save.',
      timestamp: new Date()
    });
  };

  const handleSaveFormulation = async () => {
    logger.debug('💾 Saving formulation changes...');
    
    if (!editFormData) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No changes to save',
        timestamp: new Date()
      });
      return;
    }

    setSavingFormulation(true);

    try {
      // Validate the data before saving
      const { product, components } = editFormData;
      
      if (!product.name.trim()) {
        addNotification({
          type: 'error',
          title: 'Validation Error',
          message: 'Product name cannot be empty',
          timestamp: new Date()
        });
        return;
      }
      
      if (components.length === 0) {
        addNotification({
          type: 'error',
          title: 'Validation Error',
          message: 'At least one component is required',
          timestamp: new Date()
        });
        return;
      }
      
      // Check for empty component names
      const emptyComponents = components.filter(comp => !comp.name.trim());
      if (emptyComponents.length > 0) {
        addNotification({
          type: 'error',
          title: 'Validation Error',
          message: 'All component names must be filled',
          timestamp: new Date()
        });
        return;
      }

      // Validate all components exist in Chemical Management
      const { missing, foundMap } = await getMissingComponentChemicals(components);
      if (missing.length > 0) {
        addNotification({
          type: 'error',
          title: 'Chemical Not Found',
          message: `These chemicals are not in Chemical Management: ${missing.join(', ')}. Please add them first, then save the formulation.`,
          timestamp: new Date()
        });
        return;
      }
      
      logger.debug('📊 Changes to save:', editFormData);
      logger.debug('📦 Product:', product);
      logger.debug('🧪 Components:', components);
      
      const productId = selectedFormulationProduct.product.id;
      
      // Step 1: Update the product information
      const productUpdateResponse = await fetch(`${API_BASE}/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: product.name,
          base_composition_qty: product.base_composition_qty,
          unit: product.unit
        })
      });
      
      if (!productUpdateResponse.ok) {
        const errorData = await productUpdateResponse.json();
        throw new Error(`Failed to update product: ${errorData.detail || 'Unknown error'}`);
      }
      
      logger.debug('✅ Product updated successfully');
      
      // Step 2: Update formulations
      // For simplicity, we'll delete all existing formulations and create new ones
      // This ensures consistency and handles additions/deletions/modifications
      
      // Get current formulations for this product
      const currentFormulations = selectedFormulationProduct.components;
      
      // Delete existing formulations
      for (const formulation of currentFormulations) {
        const deleteResponse = await fetch(`${API_BASE}/formulations/${formulation.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!deleteResponse.ok) {
          logger.warn(`⚠️ Failed to delete formulation ${formulation.id}`);
        }
      }
      
      logger.debug('🗑️ Deleted existing formulations');
      
      // Create new formulations
      for (const component of components) {
        const existingChemical = foundMap.get(component.name.trim().toLowerCase());
        if (!existingChemical) {
          addNotification({
            type: 'error',
            title: 'Chemical Not Found',
            message: `Chemical "${component.name}" is not in Chemical Management. Please add it first.`,
            timestamp: new Date()
          });
          return;
        }

        const chemicalId = existingChemical.id;
        
        // Create formulation
        const formulationResponse = await fetch(`${API_BASE}/formulations/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            product_id: productId,
            component_chemical_id: chemicalId,
            quantity_required: component.quantity,
            unit: component.unit
          })
        });
        
        if (!formulationResponse.ok) {
          const errorData = await formulationResponse.json();
          logger.error(`❌ Failed to create formulation for ${component.name}:`, errorData);
        } else {
          logger.debug(`✅ Created formulation for ${component.name}`);
        }
      }
      
      logger.debug('✅ All formulations updated successfully');
      
      addNotification({
        type: 'success',
        title: 'Changes Saved',
        message: `Formulation "${product.name}" updated successfully with ${components.length} components!`,
        timestamp: new Date()
      });
      
      // Exit edit mode
      setEditingFormulationProduct(null);
      
      // Refresh the data
      await loadInitialData();
      
    } catch (error) {
      logger.error('❌ Error saving formulation:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to save changes: ${error.message}`,
        timestamp: new Date()
      });
    } finally {
      setSavingFormulation(false);
    }
  };

  const handleCancelEdit = () => {
    logger.debug('❌ Canceling edit mode...');
    
    // Reset to original data
    if (selectedFormulationProduct) {
      setEditFormData({
        product: {
          name: selectedFormulationProduct.product.name,
          base_composition_qty: selectedFormulationProduct.product.base_composition_qty,
          unit: selectedFormulationProduct.product.unit
        },
        components: selectedFormulationProduct.components.map(comp => ({
          name: comp.chemical_name,
          quantity: comp.quantity_required,
          unit: comp.unit
        }))
      });
    }
    
    // Exit edit mode
    setEditingFormulationProduct(null);
    
    addNotification({
      type: 'info',
      title: 'Edit Cancelled',
      message: 'Changes have been discarded',
      timestamp: new Date()
    });
  };

  const handleAddComponentToFormulation = () => {
    logger.debug('➕ Adding new component to formulation...');
    logger.debug('🔍 editFormData:', editFormData);
    logger.debug('🔍 selectedFormulationProduct:', selectedFormulationProduct);
    
    if (!editFormData) {
      logger.error('❌ editFormData is null or undefined');
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Cannot add component: Edit data not initialized. Please try again.',
        timestamp: new Date()
      });
      return;
    }
    
    if (!selectedFormulationProduct) {
      logger.error('❌ selectedFormulationProduct is null or undefined');
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Cannot add component: Product data not available.',
        timestamp: new Date()
      });
      return;
    }
    
    const newComponent = {
      name: '',
      quantity: 0,
      unit: selectedFormulationProduct.product.unit // Use same unit as product
    };
    
    logger.debug('➕ Adding new component:', newComponent);
    
    setEditFormData(prev => ({
      ...prev,
      components: [...prev.components, newComponent]
    }));
    
    addNotification({
      type: 'success',
      title: 'Component Added',
      message: 'New component added. Fill in the details and save.',
      timestamp: new Date()
    });
  };

  const handleDeleteComponent = (index) => {
    logger.debug(`🗑️ Deleting component at index: ${index}`);
    
    if (!editFormData) return;
    
    const componentName = editFormData.components[index]?.name || `Component ${index + 1}`;
    
    if (window.confirm(`Are you sure you want to delete "${componentName}" from this formulation?`)) {
      setEditFormData(prev => ({
        ...prev,
        components: prev.components.filter((_, i) => i !== index)
      }));
      
      addNotification({
        type: 'success',
        title: 'Component Deleted',
        message: `"${componentName}" has been removed from the formulation.`,
        timestamp: new Date()
      });
    }
  };

  const handleDeleteFormulationProduct = async (productId) => {
    // Find the product name for confirmation message
    const product = chemicalProducts.find(p => p.id === productId);
    const productName = product ? product.name : `Product ID ${productId}`;
    
    if (window.confirm(`Are you sure you want to delete "${productName}" and all its formulation components?\n\nThis will permanently delete:\n- The chemical product\n- All linked formulations\n- All component relationships\n\nThis action cannot be undone.`)) {
      try {
        logger.debug(`🗑️ Deleting product: ${productName} (ID: ${productId})`);
        
        const response = await fetch(`${API_BASE}/products/${productId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          logger.debug('✅ Delete successful:', result);
          
          // Refresh the data
          await loadInitialData();
          
          addNotification({
            type: 'success',
            title: 'Product Deleted',
            message: `"${productName}" and ${result.deleted_formulations} formulation components deleted successfully`,
            timestamp: new Date()
          });
        } else {
          const errorData = await response.json();
          logger.error('❌ Delete failed:', errorData);
          throw new Error(errorData.detail || 'Failed to delete product');
        }
      } catch (err) {
        logger.error('❌ Error deleting formulation product:', err);
        addNotification({
          type: 'error',
          title: 'Delete Failed',
          message: `Could not delete "${productName}": ${err.message}`,
          timestamp: new Date()
        });
      }
    }
  };

  // Simple data display - no complex filtering
  logger.debug('Chemicals data:', {
    total: chemicals.length,
    chemicals: chemicals
  });
  logger.debug('Chemicals state type:', typeof chemicals);
  logger.debug('Chemicals state is array:', Array.isArray(chemicals));
  
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Chemical Inventory...</p>
      </div>
    );
  }
  
  return (
    <div className={styles.dashboard}>
      {/* Header */}
      <div className={styles.header}>
        <h1>Chemical Inventory Management</h1>
        <div className={styles.headerActions}>
          <div className={styles.connectionStatus}>
            <span className={`${styles.statusDot} ${wsConnected ? styles.connected : styles.disconnected}`}></span>
            {wsConnected ? 'Live Connected' : 'Disconnected'}
          </div>
          <button 
            className={styles.refreshBtn}
            onClick={loadInitialData}
            title="Refresh Data"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>
      
      {/* Notifications */}
      {notifications.length > 0 && (
        <div className={styles.notifications}>
          {notifications.map((notification, index) => (
            <div key={index} className={`${styles.notification} ${styles[notification.type]}`}>
              <span className={styles.notificationTitle}>{notification.title}</span>
              <span className={styles.notificationMessage}>{notification.message}</span>
              <button 
                className={styles.notificationClose}
                onClick={() => setNotifications(prev => prev.filter((_, i) => i !== index))}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Tab Navigation */}
      <div className={isMobile ? styles.tabNavigationMobile : styles.tabNavigation}>
        <button
          className={`${styles.tab} ${activeTab === 'chemicals' ? styles.active : ''}`}
          onClick={() => setActiveTab('chemicals')}
        >
          <FlaskConical size={20} />
          Chemicals Management
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'formulations' ? styles.active : ''}`}
          onClick={() => setActiveTab('formulations')}
        >
          <FileSpreadsheet size={20} />
          Formulations Management
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'assignments' ? styles.active : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          <Users size={20} />
          Product Assignment
        </button>
      </div>
      
      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* Chemicals Management Tab */}
        {activeTab === 'chemicals' && (
          <div className={styles.chemicalsTab}>
            <div className={styles.tabHeader}>
              <h2>Chemicals Management</h2>
              <div className={styles.headerActions}>
                <button 
                  className={styles.refreshBtn}
                  onClick={loadInitialData}
                  title="Refresh Data"
                >
                  <RefreshCw size={20} />
                  Refresh
                </button>
                {selectedChemicalIds.size > 0 && (
                  <button
                    className={styles.bulkDeleteBtn}
                    onClick={handleBulkDeleteChemicals}
                    disabled={bulkDeleting}
                    title="Delete selected chemicals"
                  >
                    <Trash2 size={20} />
                    {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedChemicalIds.size})`}
                  </button>
                )}
                {chemicalsTotal > chemicals.length && (
                  <button
                    className={styles.selectAllBtn}
                    onClick={handleSelectAllMatching}
                    title="Select all chemicals matching current search/filter"
                  >
                    Select all {chemicalsTotal}
                  </button>
                )}
                <button 
                  className={styles.uploadBtn}
                  onClick={() => setShowChemicalExcelUpload(true)}
                >
                  <Upload size={20} />
                  Upload Excel
                </button>
                <button 
                  className={styles.addButton}
                  onClick={() => setShowChemicalForm(true)}
                >
                  <Plus size={20} />
                  Add Chemical
                </button>
              </div>
            </div>
            
            {/* Search and Filters */}
            <div className={styles.searchFilters}>
              <div className={styles.searchBox}>
                <Search size={20} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search chemicals by name..."
                  value={chemicalSearchTerm}
                  onChange={(e) => setChemicalSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                {chemicalSearchTerm && (
                  <button
                    className={styles.clearSearchBtn}
                    onClick={() => setChemicalSearchTerm('')}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              
              <select
                value={chemicalFilterType}
                onChange={(e) => setChemicalFilterType(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Types</option>
                <option value="raw">Raw Materials</option>
                <option value="manufactured">Manufactured</option>
                <option value="below_threshold">Below Threshold</option>
              </select>
            </div>
            
            {/* Chemicals Table */}
            <div className={styles.tableContainer}>
              {(() => {
                const filteredChemicals = getFilteredChemicals();
                const paginatedChemicals = getPaginatedChemicals();
                
                return filteredChemicals.length === 0 ? (
                  <div className={styles.noData}>
                    <p>
                      {chemicals.length === 0 
                        ? `No chemicals found. ${loading ? 'Loading...' : 'No data available.'}`
                        : `No chemicals match your search criteria. Showing 0 of ${chemicals.length} chemicals.`
                      }
                    </p>
                    {(chemicalSearchTerm || chemicalFilterType !== 'all') && (
                      <button
                        className={styles.clearFiltersBtn}
                        onClick={() => {
                          setChemicalSearchTerm('');
                          setChemicalFilterType('all');
                        }}
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                ) : (
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>
                          <input
                          type="checkbox"
                          checked={
                            chemicals.length >0 &&
                            chemicals.every(c => selectedChemicalIds.has(c.id))
                          }
                          onChange={toggleSelectPage}
                          title="Select all on this page"
                          />
                        </th>
                        <th>Name</th>
                        <th>Unit</th>
                        <th>Available Qty</th>
                        <th>Threshold Qty</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedChemicals.map(chemical => (
                      <tr key={chemical.id}
                          className={selectedChemicalIds.has(chemical.id)? styles.selectedRow: ''}
                      >
                        <td>
                          <input
                          type="checkbox"
                          checked={selectedChemicalIds.has(chemical.id)}
                          onChange={()=> toggleChemicalSelection(chemical.id)}
                          />
                        </td>
                        <td>{chemical.name}</td>
                        <td>{chemical.unit}</td>
                        <td className={chemical.available_qty <= chemical.threshold_qty ? styles.lowStock : ''}>
                          {chemical.available_qty}
                        </td>
                        <td>{chemical.threshold_qty}</td>
                        <td>
                          <span className={`${styles.typeBadge} ${chemical.is_manufactured ? styles.manufactured : styles.raw}`}>
                            {chemical.is_manufactured ? 'Manufactured' : 'Raw'}
                          </span>
                        </td>
                        <td>
                          <div className={styles.actionButtons}>
                            <button
                              className={styles.editBtn}
                              onClick={() => setEditingChemical(chemical)}
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteChemical(chemical.id)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
              
              {/* Pagination Controls */}
              {(() => {
                const totalPages = Math.ceil(chemicalsTotal / chemicalsPerPage);
                
                return totalPages > 1 && (
                  <div className={styles.pagination}>
                    <div className={styles.paginationInfo}>
                      Showing {chemicalsTotal === 0 ? 0 : (((chemicalsCurrentPage - 1) * chemicalsPerPage) + 1)} to {Math.min(chemicalsCurrentPage * chemicalsPerPage, chemicalsTotal)} of {chemicalsTotal} chemicals
                    </div>
                    <div className={styles.paginationControls}>
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setChemicalsCurrentPage(1)}
                        disabled={chemicalsCurrentPage === 1}
                        title="First page"
                      >
                        <ChevronsLeft size={16} />
                      </button>
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setChemicalsCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={chemicalsCurrentPage === 1}
                        title="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      
                      <div className={styles.pageNumbers}>
                        {(() => {
                          const pages = [];
                          const maxVisiblePages = 5;
                          let startPage = Math.max(1, chemicalsCurrentPage - Math.floor(maxVisiblePages / 2));
                          let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                          
                          if (endPage - startPage + 1 < maxVisiblePages) {
                            startPage = Math.max(1, endPage - maxVisiblePages + 1);
                          }
                          
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                className={`${styles.pageBtn} ${chemicalsCurrentPage === i ? styles.activePage : ''}`}
                                onClick={() => setChemicalsCurrentPage(i)}
                              >
                                {i}
                              </button>
                            );
                          }
                          return pages;
                        })()}
                      </div>
                      
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setChemicalsCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={chemicalsCurrentPage === totalPages}
                        title="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setChemicalsCurrentPage(totalPages)}
                        disabled={chemicalsCurrentPage === totalPages}
                        title="Last page"
                      >
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        
        {/* Formulations Management Tab */}
        {activeTab === 'formulations' && (
          <div className={styles.formulationsTab}>
            <div className={styles.tabHeader}>
              <h2>Formulations Management</h2>
              <div className={styles.headerActions}>
                <button 
                  className={styles.refreshBtn}
                  onClick={loadInitialData}
                  title="Refresh Data"
                >
                  <RefreshCw size={20} />
                  Refresh
                </button>
                <button 
                  className={styles.uploadBtn}
                  onClick={() => setShowExcelUpload(true)}
                >
                  <Upload size={20} />
                  Upload Excel
                </button>
                <button 
                  className={styles.addButton}
                  onClick={() => setShowFormulationForm(true)}
                >
                  <Plus size={20} />
                  Add Formulation
                </button>
              </div>
            </div>
            
            {/* Search */}
            <div className={styles.searchFilters}>
              <div className={styles.searchBox}>
                <Search size={20} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search products by name..."
                  value={formulationSearchTerm}
                  onChange={(e) => setFormulationSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                {formulationSearchTerm && (
                  <button
                    className={styles.clearSearchBtn}
                    onClick={() => setFormulationSearchTerm('')}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            
            {/* Excel Preview */}
            {excelPreview && excelEditData && (
              <div className={styles.excelPreview}>
                <h3>Excel Preview - Edit & Approve</h3>
                
                {/* Product Information */}
                <div className={styles.previewCard}>
                  <div className={styles.cardHeader}>
                    <h4>Product Information</h4>
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.infoRow}>
                      <label>Product Name:</label>
                      <input
                        type="text"
                        value={excelEditData.product_name}
                        onChange={(e) => setExcelEditData(prev => ({ ...prev, product_name: e.target.value }))}
                        className={styles.editInput}
                      />
                    </div>
                    <div className={styles.infoRow}>
                      <label>Base Composition ({excelEditData.unit}):</label>
                      <div className={styles.editQuantityGroup}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={excelEditData.base_composition_qty}
                          onChange={(e) => setExcelEditData(prev => ({ ...prev, base_composition_qty: parseFloat(e.target.value) || 0 }))}
                          className={styles.editInput}
                        />
                        <button type="button" className={styles.autoBtn} onClick={setExcelBaseToSum}>Auto</button>
                      </div>
                    </div>
                    <div className={styles.infoRow}>
                      <label>Components Total:</label>
                      <span className={styles.totalValue}>{getExcelComponentsTotal()} {excelEditData.unit}</span>
                    </div>
                  </div>
                </div>

                {/* Components Table */}
                <div className={styles.previewCard}>
                  <div className={styles.cardHeader}>
                    <h4>Component Chemicals ({excelEditData.components.length} components)</h4>
                    <button className={styles.addButton} type="button" onClick={addExcelComponent}>
                      <Plus size={16}/> Add Component
                    </button>
                  </div>
                  
                  <div className={styles.componentsTableContainer}>
                    <table className={styles.componentsTable}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Chemical Code</th>
                          <th>Quantity</th>
                          <th>Unit</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {excelEditData.components.map((comp, index) => (
                          <tr key={index}>
                            <td className={styles.serialNumber}>{index + 1}</td>
                            <td>
                              <input
                                type="text"
                                value={comp.code}
                                onChange={(e) => updateExcelComponentCode(index, e.target.value)}
                                placeholder="Chemical code"
                                className={styles.tableInput}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={comp.quantity}
                                onChange={(e) => updateExcelComponentQty(index, e.target.value)}
                                className={styles.tableInput}
                              />
                            </td>
                            <td className={styles.unitCell}>{comp.unit}</td>
                            <td>
                              <button 
                                className={styles.deleteBtn} 
                                type="button" 
                                onClick={() => deleteExcelComponent(index)} 
                                title="Delete component"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Validation Messages */}
                {excelValidationMsg && (
                  <div className={styles.validationMessage}>{excelValidationMsg}</div>
                )}

                <div className={styles.previewActions}>
                  <button 
                    className={styles.approveBtn}
                    onClick={handleApproveExcel}
                    disabled={uploading || !!validateExcelEditData()}
                  >
                    {uploading ? 'Processing...' : 'Approve & Create'}
                  </button>
                  <button 
                    className={styles.cancelBtn}
                    onClick={() => {
                      setExcelPreview(null);
                      setExcelData(null);
                      setExcelEditData(null);
                      setExcelValidationMsg('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
                        {/* Formulations Table - Product Names Only */}
            <div className={styles.tableContainer}>
              {formulations.length === 0 && (
                <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px' }}>
                  <p><strong>No Formulations Found</strong></p>
                  <p>To see formulations here, you need to:</p>
                  <ol style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    <li>Create a chemical product first (in Chemicals Management)</li>
                    <li>Add component chemicals</li>
                    <li>Create a formulation linking them</li>
                  </ol>
                  <button 
                    onClick={() => setShowFormulationForm(true)}
                    style={{ 
                      background: '#10b981', 
                      color: 'white', 
                      border: 'none', 
                      padding: '0.5rem 1rem', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      marginTop: '0.5rem'
                    }}
                  >
                    Create Your First Formulation
                  </button>
                </div>
              )}
              
              {(() => {
                const filteredFormulations = getFilteredFormulations();
                const paginatedFormulations = getPaginatedFormulations();
                
                return filteredFormulations.length === 0 ? (
                  <div className={styles.noData}>
                    <p>
                      {getUniqueFormulationProducts().length === 0 
                        ? `No chemical products found. ${loading ? 'Loading...' : 'No data available.'}`
                        : `No products match your search criteria. Showing 0 of ${filteredFormulations.length} products.`
                      }
                    </p>
                    {formulationSearchTerm && (
                      <button
                        className={styles.clearFiltersBtn}
                        onClick={() => {
                          setFormulationSearchTerm('');
                        }}
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                ) : (
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>Product Name</th>
                        <th>Base Composition</th>
                        <th>Components Count</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedFormulations.map(product => (
                      <tr key={product.id}>
                        <td>
                          <strong>{product.name}</strong>
                        </td>
                        <td>
                          {product.base_composition_qty} {product.unit}
                        </td>
                        <td>
                          {getFormulationComponentsCount(product.id)} components
                        </td>
                        <td>
                          <div className={styles.actionButtons}>
                            <button
                              className={styles.viewBtn}
                              onClick={() => handleViewFormulationDetails(product.id)}
                              title="View Details"
                            >
                              <Eye size={16} />
                              View Details
                            </button>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteFormulationProduct(product.id)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
              
              {/* Pagination Controls */}
              {(() => {
                const filteredFormulations = getFilteredFormulations();
                const totalPages = Math.ceil(filteredFormulations.length / formulationsPerPage);
                
                return totalPages > 1 && (
                  <div className={styles.pagination}>
                    <div className={styles.paginationInfo}>
                      Showing {((formulationsCurrentPage - 1) * formulationsPerPage) + 1} to {Math.min(formulationsCurrentPage * formulationsPerPage, filteredFormulations.length)} of {filteredFormulations.length} products
                    </div>
                    <div className={styles.paginationControls}>
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setFormulationsCurrentPage(1)}
                        disabled={formulationsCurrentPage === 1}
                        title="First page"
                      >
                        <ChevronsLeft size={16} />
                      </button>
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setFormulationsCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={formulationsCurrentPage === 1}
                        title="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      
                      <div className={styles.pageNumbers}>
                        {(() => {
                          const pages = [];
                          const maxVisiblePages = 5;
                          let startPage = Math.max(1, formulationsCurrentPage - Math.floor(maxVisiblePages / 2));
                          let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                          
                          if (endPage - startPage + 1 < maxVisiblePages) {
                            startPage = Math.max(1, endPage - maxVisiblePages + 1);
                          }
                          
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                className={`${styles.pageBtn} ${formulationsCurrentPage === i ? styles.activePage : ''}`}
                                onClick={() => setFormulationsCurrentPage(i)}
                              >
                                {i}
                              </button>
                            );
                          }
                          return pages;
                        })()}
                      </div>
                      
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setFormulationsCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={formulationsCurrentPage === totalPages}
                        title="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setFormulationsCurrentPage(totalPages)}
                        disabled={formulationsCurrentPage === totalPages}
                        title="Last page"
                      >
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        
        {/* Product Assignment Tab */}
        {activeTab === 'assignments' && (
            <ProductAssignmentTab userInfo={userInfo} wsConnected={wsConnected} />
        )}
      </div>
      
      {/* Modals */}
      {(showChemicalForm || editingChemical) && (
        <ChemicalFormModal
          chemical={editingChemical}
          onSubmit={editingChemical ? 
            (data) => handleUpdateChemical(editingChemical.id, data) : 
            handleCreateChemical
          }
          onClose={() => {
            setShowChemicalForm(false);
            setEditingChemical(null);
          }}
        />
      )}
      
      {showFormulationForm && (
        <FormulationFormModal
          formulation={editingFormulation}
          chemicals={chemicals}
          products={chemicalProducts}
          findChemicalByName={findChemicalByName}
          onNotify={addNotification}
          onSubmit={editingFormulation ? 
            (data) => handleUpdateFormulation(editingFormulation.id, data) : 
            handleCreateFormulation
          }
          onClose={() => {
            setShowFormulationForm(false);
            setEditingFormulation(null);
          }}
        />
      )}
      
      {showAssignmentForm && (
        <AssignmentFormModal
          isOpen={showAssignmentForm}
          products={chemicalProducts}
          users={users}
          formulations={formulations}
          chemicals={chemicals}
          onSubmit={handleCreateAssignment}
          onClose={() => {
            setShowAssignmentForm(false);
            setEditingAssignment(null);
          }}
          loading={loading}
        />
      )}
      
      {/* Assignment Details Modal */}
      {showAssignmentDetails && currentAssignment && (
        <div className={styles.modalOverlay}>
          <div className={styles.assignmentDetailsModal}>
            <div className={styles.modalHeader}>
              <div className={styles.headerContent}>
                <div className={styles.productTitle}>
                  <h3>📦 {currentAssignment.product_name}</h3>
                  <span className={styles.productSubtitle}>Assignment Details</span>
                </div>
                <button 
                  className={styles.closeBtn} 
                  onClick={() => setShowAssignmentDetails(false)}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className={styles.modalContent}>
              {/* Assignment Info */}
              <div className={styles.infoCard}>
                <div className={styles.cardHeader}>
                  <h4>📋 Assignment Information</h4>
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Assigned to:</span>
                    <span className={styles.infoValue}>{currentAssignment.assigned_to_name}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Target Quantity:</span>
                    <span className={styles.infoValue}>{currentAssignment.target_quantity} {currentAssignment.target_unit}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Base Composition:</span>
                    <span className={styles.infoValue}>{currentAssignment.baseComposition} {currentAssignment.baseUnit}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Time Limit:</span>
                    <span className={styles.infoValue}>{currentAssignment.timeLimit} minutes</span>
                  </div>
                </div>
              </div>
              
              {/* Components List */}
              <div className={styles.infoCard}>
                <div className={styles.cardHeader}>
                  <h4>🧪 Formulation Components</h4>
                  <span className={styles.cardSubtitle}>Scaled quantities for target production</span>
                </div>
                <div className={styles.cardContent}>
                  {currentAssignment.components && currentAssignment.components.length > 0 ? (
                    <div className={styles.componentsList}>
                      {currentAssignment.components.map((component, index) => {
                        // Use the correct field names from the formulation components API
                        const isCompleted = component.completed || component.status === 'COMPLETED';
                        const chemicalName = component.chemical_name || component.component_name || `Chemical ID ${component.component_chemical_id}`;
                        const quantity = component.scaled_quantity || component.quantity_required || 0;
                        const unit = component.unit || 'g';
                        
                        return (
                          <div key={component.id || index} className={styles.componentItem}>
                            <div className={styles.componentInfo}>
                              <div className={styles.componentName}>
                                {chemicalName}
                              </div>
                              <div className={styles.componentQuantity}>
                                {quantity.toFixed(2)} {unit}
                              </div>
                              <div className={styles.componentScaled}>
                                Scaled Quantity: {quantity.toFixed(2)} {unit}
                              </div>
                            </div>
                            <div className={`${styles.componentStatus} ${isCompleted ? styles.completed : styles.pending}`}>
                              {isCompleted ? (
                                <CheckCircle size={20} className={styles.completedIcon} />
                              ) : (
                                <XCircle size={20} className={styles.pendingIcon} />
                              )}
                              <span>{isCompleted ? 'Added' : 'Pending'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.noComponents}>
                      <p>No formulation components found for this product.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Formulation Details Modal */}
      {showFormulationDetails && selectedFormulationProduct && (
        <div className={styles.modalOverlay}>
          <div className={styles.formulationDetailsModal}>
            <div className={styles.modalHeader}>
              <div className={styles.headerContent}>
                <div className={styles.productTitle}>
                  <h3>📦 {selectedFormulationProduct.product.name}</h3>
                  <span className={styles.productSubtitle}>Formulation Details</span>
                </div>
                <button 
                  className={styles.closeBtn}
                  onClick={() => setShowFormulationDetails(false)}
                  title="Close"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className={styles.modalContent}>
              {/* Product Information Card */}
              <div className={styles.infoCard}>
                <div className={styles.cardHeader}>
                  <h4>📊 Product Information</h4>
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Product Name:</span>
                    {editingFormulationProduct === selectedFormulationProduct.product.id ? (
                      <input
                        type="text"
                        value={editFormData?.product?.name || selectedFormulationProduct.product.name}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          product: { ...prev?.product, name: e.target.value }
                        }))}
                        className={styles.editInput}
                      />
                    ) : (
                      <span className={styles.infoValue}>{selectedFormulationProduct.product.name}</span>
                    )}
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Base Composition:</span>
                    {editingFormulationProduct === selectedFormulationProduct.product.id ? (
                      <div className={styles.editQuantityGroup}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData?.product?.base_composition_qty || selectedFormulationProduct.product.base_composition_qty}
                          onChange={(e) => setEditFormData(prev => ({
                            ...prev,
                            product: { ...prev?.product, base_composition_qty: parseFloat(e.target.value) || 0 }
                          }))}
                          className={styles.editInput}
                        />
                        <span className={styles.unit}>{selectedFormulationProduct.product.unit}</span>
                      </div>
                    ) : (
                      <span className={styles.infoValue}>
                        <strong>{selectedFormulationProduct.product.base_composition_qty} {selectedFormulationProduct.product.unit}</strong>
                      </span>
                    )}
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Components:</span>
                    <span className={styles.infoValue}>
                      <strong>{selectedFormulationProduct.components.length} chemicals</strong>
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Quantity Scaling Card */}
              <div className={styles.infoCard}>
                <div className={styles.cardHeader}>
                  <h4>⚖️ Quantity Scaling</h4>
                  <span className={styles.cardSubtitle}>Scale the formulation to different quantities</span>
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.scalingControls}>
                    <div className={styles.scalingInput}>
                      <label>Target Quantity:</label>
                      <div className={styles.inputGroup}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={scaledQuantity || 0}
                          onChange={(e) => setScaledQuantity(parseFloat(e.target.value) || 0)}
                          placeholder="Enter target quantity"
                        />
                        <span className={styles.unit}>{selectedFormulationProduct.product.unit}</span>
                      </div>
                    </div>
                    <button 
                      className={styles.resetBtn}
                      onClick={() => setScaledQuantity(selectedFormulationProduct.product.base_composition_qty)}
                      title="Reset to base composition"
                    >
                      <RefreshCw size={16} />
                      Reset to Base
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Components List Card */}
              <div className={styles.infoCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.headerLeft}>
                    <h4>🧪 Component Chemicals</h4>
                    <span className={styles.cardSubtitle}>Chemical components and their quantities</span>
                  </div>
                  {editingFormulationProduct === selectedFormulationProduct.product.id && (
                    <button 
                      className={styles.addComponentBtn}
                      onClick={() => handleAddComponentToFormulation()}
                      title="Add new component chemical"
                    >
                      <Plus size={16} />
                      Add Component
                    </button>
                  )}
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.componentsGrid}>
                    {(editingFormulationProduct === selectedFormulationProduct.product.id && editFormData 
                      ? editFormData.components 
                      : selectedFormulationProduct.components
                    ).map((component, index) => {
                      // Safety check to ensure scaledQuantity is properly initialized
                      if (!scaledQuantity || !selectedFormulationProduct.product.base_composition_qty) {
                        const isEditMode = editingFormulationProduct === selectedFormulationProduct.product.id && editFormData;
                        const componentName = isEditMode ? component.name : component.chemical_name;
                        const componentQuantity = isEditMode ? component.quantity : component.quantity_required;
                        const componentUnit = component.unit;
                        const componentId = component.id || `new-${index}`;
                        
                        return (
                          <div key={componentId} className={styles.componentCard}>
                            <div className={styles.componentHeader}>
                              <span className={styles.componentName}>{componentName}</span>
                              <div className={styles.componentActions}>
                                <span className={styles.componentId}>#{componentId}</span>
                                {isEditMode && (
                                  <button
                                    className={styles.deleteComponentBtn}
                                    onClick={() => handleDeleteComponent(index)}
                                    title="Delete this component"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className={styles.componentQuantities}>
                              <div className={styles.quantityRow}>
                                <span className={styles.quantityLabel}>Original:</span>
                                <span className={styles.quantityValue}>
                                  {componentQuantity} {componentUnit}
                                </span>
                              </div>
                              <div className={styles.quantityRow}>
                                <span className={styles.quantityLabel}>Scaled:</span>
                                <span className={`${styles.quantityValue} ${styles.loading}`}>
                                  Loading...
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      const isEditMode = editingFormulationProduct === selectedFormulationProduct.product.id && editFormData;
                      const componentQuantity = isEditMode ? component.quantity : component.quantity_required;
                      const componentId = component.id || `new-${index}`;
                      
                      const scaleFactor = scaledQuantity / selectedFormulationProduct.product.base_composition_qty;
                      const scaledComponentQty = componentQuantity * scaleFactor;
                      
                      return (
                        <div key={componentId} className={styles.componentCard}>
                          <div className={styles.componentHeader}>
                            {editingFormulationProduct === selectedFormulationProduct.product.id ? (
                              <input
                                type="text"
                                value={editFormData?.components?.[index]?.name || component.chemical_name}
                                onChange={(e) => setEditFormData(prev => ({
                                  ...prev,
                                  components: prev.components.map((comp, i) => 
                                    i === index ? { ...comp, name: e.target.value } : comp
                                  )
                                }))}
                                className={styles.editInput}
                                placeholder="Chemical name"
                              />
                            ) : (
                              <span className={styles.componentName}>{component.chemical_name}</span>
                            )}
                            <div className={styles.componentActions}>
                              <span className={styles.componentId}>#{componentId}</span>
                              {editingFormulationProduct === selectedFormulationProduct.product.id && (
                                <button
                                  className={styles.deleteComponentBtn}
                                  onClick={() => handleDeleteComponent(index)}
                                  title="Delete this component"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className={styles.componentQuantities}>
                            <div className={styles.quantityRow}>
                              <span className={styles.quantityLabel}>Quantity:</span>
                              {editingFormulationProduct === selectedFormulationProduct.product.id ? (
                                <div className={styles.editQuantityGroup}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={componentQuantity}
                                    onChange={(e) => setEditFormData(prev => ({
                                      ...prev,
                                      components: prev.components.map((comp, i) => 
                                        i === index ? { ...comp, quantity: parseFloat(e.target.value) || 0 } : comp
                                      )
                                    }))}
                                    className={styles.editInput}
                                  />
                                  <span className={styles.unit}>{component.unit}</span>
                                </div>
                              ) : (
                                <span className={styles.quantityValue}>
                                  {componentQuantity} {component.unit}
                                </span>
                              )}
                            </div>
                            <div className={styles.quantityRow}>
                              <span className={styles.quantityLabel}>Scaled:</span>
                              <span className={`${styles.quantityValue} ${styles.scaled}`}>
                                {scaledComponentQty.toFixed(2)} {component.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className={styles.modalActions}>
                {editingFormulationProduct === selectedFormulationProduct.product.id ? (
                  <>
                    <button 
                      className={styles.saveBtn}
                      onClick={() => handleSaveFormulation()}
                      disabled={savingFormulation}
                      title="Save changes"
                    >
                      {savingFormulation ? (
                        <>
                          <RefreshCw size={18} className={styles.spinning} />
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={18} />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button 
                      className={styles.cancelBtn}
                      onClick={() => handleCancelEdit()}
                      title="Cancel editing"
                    >
                      <X size={18} />
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      className={styles.editBtn}
                      onClick={() => handleEditFormulation(selectedFormulationProduct.product.id)}
                      title="Edit this formulation"
                    >
                      <Edit size={18} />
                      Edit Formulation
                    </button>
                    <button 
                      className={styles.deleteBtn}
                      onClick={() => {
                        handleDeleteFormulationProduct(selectedFormulationProduct.product.id);
                        setShowFormulationDetails(false);
                      }}
                      title="Delete this formulation"
                    >
                      <Trash2 size={18} />
                      Delete Formulation
                    </button>
                  </>
                )}
                <button 
                  className={styles.closeBtn}
                  onClick={() => setShowFormulationDetails(false)}
                  title="Close modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showExcelUpload && (
        <ExcelUploadModal
          onUpload={handleExcelUpload}
          onClose={() => setShowExcelUpload(false)}
          uploading={uploading}
        />
      )}

      {showChemicalExcelUpload && (
        <ChemicalExcelUploadModal
          onUpload={handleUploadChemicalsExcel}
          onDownloadTemplate={downloadChemicalsExcelTemplate}
          onClose={() => setShowChemicalExcelUpload(false)}
          uploading={chemicalExcelUploading}
        />
      )}
    </div>
  );
}

// Modal Components
function ChemicalFormModal({ chemical, onSubmit, onClose }) {
  const [formData, setFormData] = useState({
    name: chemical?.name || '',
    unit: chemical?.unit || 'g',
    available_qty: chemical?.available_qty || 0,
    threshold_qty: chemical?.threshold_qty || 0,
    is_manufactured: chemical?.is_manufactured || false
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3>{chemical ? 'Edit Chemical' : 'Add New Chemical'}</h3>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Chemical Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <label>Unit</label>
            <select
              value={formData.unit}
              onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
            >
              <option value="g">Grams (g)</option>
              <option value="kg">Kilograms (kg)</option>
              <option value="l">Liters (L)</option>
              <option value="ml">Milliliters (mL)</option>
            </select>
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Available Quantity</label>
              <input
                type="number"
                step="0.01"
                value={formData.available_qty || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData(prev => ({ ...prev, available_qty: isNaN(value) ? 0 : value }));
                }}
                required
              />
            </div>
            
            <div className={styles.formGroup}>
              <label>Threshold Quantity</label>
              <input
                type="number"
                step="0.01"
                value={formData.threshold_qty || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData(prev => ({ ...prev, threshold_qty: isNaN(value) ? 0 : value }));
                }}
                required
              />
            </div>
          </div>
          
          <div className={styles.formGroup}>
            <label>
              <input
                type="checkbox"
                checked={formData.is_manufactured}
                onChange={(e) => setFormData(prev => ({ ...prev, is_manufactured: e.target.checked }))}
              />
              This is a manufactured chemical
            </label>
          </div>
          
          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn}>
              {chemical ? 'Update' : 'Create'} Chemical
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormulationFormModal({ formulation, chemicals, products, findChemicalByName, onNotify, onSubmit, onClose }) {
  // Product data
  const [productData, setProductData] = useState({
    name: '',
    base_composition_qty: 0,
    unit: 'g'
  });
  
  // Component data for adding new components
  const [componentData, setComponentData] = useState({
    name: '',
    quantity: 0,
    unit: 'g'
  });
  
  // All added components
  const [components, setComponents] = useState([]);
  
  // Track if we're editing
  const [editingComponentIndex, setEditingComponentIndex] = useState(null);
  const [validatingChemical, setValidatingChemical] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate we have a product and at least one component
    if (!productData.name || components.length === 0) {
      onNotify?.({
        type: 'error',
        title: 'Validation Error',
        message: 'Please provide a product name and at least one component',
        timestamp: new Date()
      });
      return;
    }
    
    // Validate that component total matches base composition
    const componentTotal = calculateTotalQuantity();
    if (Math.abs(componentTotal - productData.base_composition_qty) > 0.01) {
      onNotify?.({
        type: 'error',
        title: 'Validation Error',
        message: `Component total (${componentTotal}) must match base composition quantity (${productData.base_composition_qty})`,
        timestamp: new Date()
      });
      return;
    }

    // Final check: every component chemical must exist in Chemical Management
    if (findChemicalByName) {
      const missing = [];
      for (const component of components) {
        const chemical = await findChemicalByName(component.name);
        if (!chemical) missing.push(component.name);
      }
      if (missing.length > 0) {
        onNotify?.({
          type: 'error',
          title: 'Chemical Not Found',
          message: `These chemicals are not in Chemical Management: ${missing.join(', ')}. Please add them first.`,
          timestamp: new Date()
        });
        return;
      }
    }
    
    // Submit the complete formulation data
    onSubmit({
      product: productData,
      components: components
    });
  };
  
  const handleAddComponent = async (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    
    if (!componentData.name || componentData.quantity <= 0) {
      onNotify?.({
        type: 'error',
        title: 'Validation Error',
        message: 'Please provide chemical name and quantity',
        timestamp: new Date()
      });
      return;
    }

    // Block adding chemicals that are not in Chemical Management
    if (findChemicalByName) {
      try {
        setValidatingChemical(true);
        const existingChemical = await findChemicalByName(componentData.name);
        if (!existingChemical) {
          onNotify?.({
            type: 'error',
            title: 'Chemical Not Found',
            message: `"${componentData.name}" is not in Chemical Management. Add it there first, then use it in a formulation.`,
            timestamp: new Date()
          });
          return;
        }
      } finally {
        setValidatingChemical(false);
      }
    }
    
    if (editingComponentIndex !== null) {
      // Update existing component
      const updatedComponents = [...components];
      updatedComponents[editingComponentIndex] = { ...componentData };
      setComponents(updatedComponents);
      setEditingComponentIndex(null);
    } else {
      // Add new component
      setComponents(prev => [...prev, { ...componentData }]);
      
      // Auto-update base composition quantity if it's 0
      if (productData.base_composition_qty === 0) {
        const newTotal = calculateTotalQuantity() + componentData.quantity;
        setProductData(prev => ({ ...prev, base_composition_qty: newTotal }));
      }
    }
    
    // Reset component form
    setComponentData({ name: '', quantity: 0, unit: 'g' });
  };
  

  
  const handleEditComponent = (index) => {
    const component = components[index];
    setComponentData({ ...component });
    setEditingComponentIndex(index);
  };
  
  const handleDeleteComponent = (index) => {
    setComponents(prev => prev.filter((_, i) => i !== index));
    if (editingComponentIndex === index) {
      setEditingComponentIndex(null);
      setComponentData({ name: '', quantity: 0, unit: 'g' });
    }
  };
  
  const handleCancelEdit = () => {
    setEditingComponentIndex(null);
    setComponentData({ name: '', quantity: 0, unit: 'g' });
  };
  
  const calculateTotalQuantity = () => {
    return components.reduce((total, comp) => total + comp.quantity, 0);
  };
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal} style={{ maxWidth: '800px', maxHeight: '90vh' }}>
        <div className={styles.modalHeader}>
          <h3>{formulation ? 'Edit Formulation' : 'Create New Formulation'}</h3>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Product Information */}
          <div className={styles.formSection}>
            <h4>📦 Product Information</h4>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Product Name *</label>
                <input
                  type="text"
                  value={productData.name}
                  onChange={(e) => setProductData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., OSR16124, WRCD9374"
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Base Composition Quantity *</label>
                <div className={styles.quantityInputGroup}>
                  <input
                    type="number"
                    step="0.01"
                    value={productData.base_composition_qty}
                    onChange={(e) => setProductData(prev => ({ ...prev, base_composition_qty: parseFloat(e.target.value) || 0 }))}
                    placeholder="Total quantity for 100% composition"
                    required
                  />
                  {components.length > 0 && (
                    <button
                      type="button"
                      className={styles.autoCalculateBtn}
                      onClick={() => setProductData(prev => ({ ...prev, base_composition_qty: calculateTotalQuantity() }))}
                      title="Auto-calculate from components"
                    >
                      Auto
                    </button>
                  )}
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label>Unit *</label>
                <select
                  value={productData.unit}
                  onChange={(e) => setProductData(prev => ({ ...prev, unit: e.target.value }))}
                >
                  <option value="g">Grams (g)</option>
                  <option value="kg">Kilograms (kg)</option>
                  <option value="l">Liters (L)</option>
                  <option value="ml">Milliliters (mL)</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Component Addition */}
          <div className={styles.formSection}>
            <h4>🧪 Add Component Chemical</h4>
            <p className={styles.helpText}>
              Add components one by one. Each chemical must already exist in Chemical Management.
              The total quantity of all components should match the base composition quantity above.
            </p>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Chemical Name *</label>
                <input
                  type="text"
                  value={componentData.name}
                  onChange={(e) => setComponentData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., AP3, HP2, 2156"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Quantity *</label>
                <input
                  type="number"
                  step="0.01"
                  value={componentData.quantity}
                  onChange={(e) => setComponentData(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                  placeholder="Required quantity"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Unit *</label>
                <select
                  value={componentData.unit}
                  onChange={(e) => setComponentData(prev => ({ ...prev, unit: e.target.value }))}
                >
                  <option value="g">Grams (g)</option>
                  <option value="kg">Kilograms (kg)</option>
                  <option value="l">Liters (L)</option>
                  <option value="ml">Milliliters (mL)</option>
                </select>
              </div>
            </div>
            
            <div className={styles.componentActions}>
              {editingComponentIndex !== null ? (
                <>
                  <button 
                    type="button" 
                    className={styles.updateBtn}
                    onClick={handleAddComponent}
                    disabled={validatingChemical}
                  >
                    {validatingChemical ? 'Checking...' : 'Update Component'}
                  </button>
                  <button 
                    type="button" 
                    className={styles.cancelBtn}
                    onClick={handleCancelEdit}
                    disabled={validatingChemical}
                  >
                    Cancel Edit
                  </button>
                </>
              ) : (
                <button 
                  type="button" 
                  className={styles.addBtn}
                  onClick={handleAddComponent}
                  disabled={validatingChemical}
                >
                  {validatingChemical ? 'Checking...' : '+ Add Component'}
                </button>
              )}
            </div>
          </div>
          
          {/* Components Table */}
          {components.length > 0 && (
            <div className={styles.formSection}>
              <h4>📋 Components Summary</h4>
              
              {/* Validation Message */}
              {Math.abs(calculateTotalQuantity() - productData.base_composition_qty) > 0.01 && (
                <div className={styles.validationMessage}>
                  ⚠️ Component total ({calculateTotalQuantity()}) does not match base composition ({productData.base_composition_qty})
                </div>
              )}
              
              <div className={styles.componentsTable}>
                <table>
                  <thead>
                    <tr>
                      <th>Chemical Name</th>
                      <th>Quantity</th>
                      <th>Unit</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((component, index) => (
                      <tr key={index}>
                        <td>{component.name}</td>
                        <td>{component.quantity}</td>
                        <td>{component.unit}</td>
                        <td>
                          <button
                            type="button"
                            className={styles.editBtn}
                            onClick={() => handleEditComponent(index)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className={styles.deleteBtn}
                            onClick={() => handleDeleteComponent(index)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>Total:</strong></td>
                      <td><strong>{calculateTotalQuantity()}</strong></td>
                      <td><strong>{productData.unit}</strong></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
          
          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className={styles.submitBtn}
              disabled={!productData.name || components.length === 0 || Math.abs(calculateTotalQuantity() - productData.base_composition_qty) > 0.01}
            >
              Create Formulation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ExcelUploadModal({ onUpload, onClose, uploading }) {
  const [file, setFile] = useState(null);
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type.includes('spreadsheet')) {
      setFile(selectedFile);
    } else {
      alert('Please select a valid Excel file (.xlsx or .xls)');
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (file) {
      onUpload(file);
    }
  };
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3>Upload Excel Formulation</h3>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Select Excel File</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              required
            />
            <small>Supported formats: .xlsx, .xls</small>
          </div>
          
          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className={styles.submitBtn}
              disabled={!file || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload & Parse'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChemicalExcelUploadModal({ onUpload, onDownloadTemplate, onClose, uploading }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setResult(null);
    setError('');
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setFile(null);
      setError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }
    setFile(selectedFile);
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    setError('');
    try {
      await onDownloadTemplate();
    } catch (err) {
      setError(err.message || 'Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setError('');
    try {
      const data = await onUpload(file);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Failed to upload Excel file');
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.chemicalExcelModal}`}>
        <div className={styles.modalHeader}>
          <h3>Upload Chemicals Excel</h3>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <p className={styles.chemicalExcelHint}>
            Upload an Excel file to add multiple chemicals at once. Duplicate names are skipped.
            Columns: <strong>name</strong> (required), <strong>unit</strong>, <strong>available_qty</strong>,{' '}
            <strong>threshold_qty</strong>, <strong>is_manufactured</strong>.
          </p>

          <div className={styles.formGroup}>
            <button
              type="button"
              className={styles.templateBtn}
              onClick={handleDownloadTemplate}
              disabled={downloading}
            >
              <Download size={16} />
              {downloading ? 'Downloading…' : 'Download template'}
            </button>
          </div>

          <div className={styles.formGroup}>
            <label>Select Excel File</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
            <small>Supported formats: .xlsx, .xls</small>
          </div>

          {error && <div className={styles.chemicalExcelError}>{error}</div>}

          {result && (
            <div className={styles.chemicalExcelResults}>
              <p className={styles.chemicalExcelSummary}>{result.message}</p>
              {result.skipped_items?.length > 0 && (
                <div>
                  <h4>Skipped</h4>
                  <ul>
                    {result.skipped_items.slice(0, 10).map((item, idx) => (
                      <li key={`skip-${idx}`}>
                        Row {item.row ?? '—'}: {item.name || '(no name)'} — {item.reason}
                      </li>
                    ))}
                    {result.skipped_items.length > 10 && (
                      <li>…and {result.skipped_items.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}
              {result.error_items?.length > 0 && (
                <div>
                  <h4>Errors</h4>
                  <ul>
                    {result.error_items.slice(0, 10).map((item, idx) => (
                      <li key={`err-${idx}`}>
                        Row {item.row ?? '—'}: {item.name || '(no name)'} — {item.reason}
                      </li>
                    ))}
                    {result.error_items.length > 10 && (
                      <li>…and {result.error_items.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              {result ? 'Close' : 'Cancel'}
            </button>
            {!result && (
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={!file || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload & Create'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
